/**
 * Booking transport planning (transport rebuild) — the hook + fleet
 * autocomplete behind the Job Details "Vehicles" block (VehiclePlanBlock).
 *
 * Vehicles are PLANNING-ONLY: the selection feeds job sheets, vehicle
 * windows, and the Schedule, and never touches money. The priced Transport
 * line lives in the gear editor as one ordinary "Delivery & collection" row
 * the compute suggests and the Operator edits — selecting or deselecting a
 * truck here neither writes booking_items nor triggers a compute.
 */
import { useEffect, useRef, useState } from "react";
import { cx } from "@shared/cx";
import forms from "../ui/field.module.css";
import styles from "./TransportSection.module.css";
import { useDismissable } from "../../lib/use-dismissable";
import { useListboxNav } from "../../lib/use-listbox-nav";
import {
  listBookingVehicles,
  listVehicles,
  putBookingVehicles,
  type BookingVehicle,
  type CompanyVehicle,
} from "../../lib/transport-api";

export interface TransportController {
  loaded: boolean;
  /** Active fleet vehicles not yet selected on this booking. */
  availableFleet: CompanyVehicle[];
  /** Planned vehicles for this booking. */
  selection: BookingVehicle[];
  error: string | null;
  /** A selection save is in flight. */
  busy: boolean;
  addVehicle: (v: CompanyVehicle) => void;
  removeVehicle: (vehicleId: string) => void;
}

export function useBookingTransport({
  bookingId,
  onChanged,
}: {
  bookingId: string;
  /**
   * Runs after every selection save — the caller refreshes whatever reads
   * the selection (vehicle windows, allocation conflicts, the workspace
   * read's bookingVehicles).
   */
  onChanged: () => Promise<void>;
}): TransportController {
  const [fleet, setFleet] = useState<CompanyVehicle[]>([]);
  const [selection, setSelection] = useState<BookingVehicle[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    Promise.all([
      listVehicles(ac.signal),
      listBookingVehicles(bookingId, ac.signal),
    ])
      .then(([allVehicles, selected]) => {
        setFleet(allVehicles.filter((v) => v.active));
        setSelection(selected.vehicles);
        setLoaded(true);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
        setLoaded(true);
      });
    return () => ac.abort();
  }, [bookingId]);

  const save = async (prev: BookingVehicle[], next: BookingVehicle[]) => {
    setSelection(next);
    setError(null);
    setBusy(true);
    try {
      const result = await putBookingVehicles(
        bookingId,
        next.map((v) => ({ vehicle_id: v.vehicle_id })),
      );
      setSelection(result.vehicles);
      await onChanged();
    } catch (err) {
      // The save failed — the optimistic selection is not real.
      setSelection(prev);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const addVehicle = (vehicle: CompanyVehicle) => {
    if (selection.some((s) => s.vehicle_id === vehicle.id)) return;
    void save(selection, [
      ...selection,
      {
        vehicle_id: vehicle.id,
        vehicle_name: vehicle.name,
        vehicle_type: vehicle.vehicle_type,
        sort_order: selection.length,
      },
    ]);
  };

  const removeVehicle = (vehicleId: string) => {
    const next = selection.filter((s) => s.vehicle_id !== vehicleId);
    if (next.length === selection.length) return;
    void save(selection, next);
  };

  return {
    loaded,
    availableFleet: fleet.filter(
      (v) => !selection.some((s) => s.vehicle_id === v.id),
    ),
    selection,
    error,
    busy,
    addVehicle,
    removeVehicle,
  };
}

/**
 * Fleet autocomplete — the vehicle counterpart of EquipmentAutocomplete,
 * used as the planning block's vehicle search. Free text is not
 * committable — vehicles come from the fleet DB, so blur reverts to the
 * current value.
 */
export function VehicleAutocomplete({
  options,
  onSelect,
  value = "",
  autoFocus,
  className,
  disabled,
  clearOnSelect,
}: {
  options: CompanyVehicle[];
  onSelect: (v: CompanyVehicle) => void;
  value?: string;
  autoFocus?: boolean;
  className?: string;
  /** A save is in flight — the selection must not change under it. */
  disabled?: boolean;
  /** Picking appends to a list rather than setting this field's value, so the
   * query empties for the next search (the crew picker's behaviour). */
  clearOnSelect?: boolean;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(autoFocus ?? false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Dismissing (outside click or Escape) also resets a half-typed query back
  // to the current vehicle, matching the pre-hook behaviour.
  useDismissable({
    open,
    onDismiss: () => {
      setOpen(false);
      setQuery(value);
    },
    pointer: [wrapperRef],
  });

  // An untouched input (query still the current vehicle) offers the whole
  // fleet; typing filters it.
  const matches =
    query === value
      ? options
      : options.filter((v) =>
          v.name.toLowerCase().includes(query.trim().toLowerCase()),
        );

  const pick = (v: CompanyVehicle) => {
    setOpen(false);
    setQuery(clearOnSelect ? "" : v.name);
    onSelect(v);
  };

  const nav = useListboxNav({
    open: open && matches.length > 0,
    count: matches.length,
    onCommit: (i) => pick(matches[i]),
  });

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <input
        type="text"
        aria-label="Search vehicles"
        placeholder="Search vehicles..."
        autoFocus={autoFocus}
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={nav.onKeyDown}
        {...nav.comboboxProps}
        className={cx(className ?? forms.inputCompact, styles.input)}
      />
      {open && !disabled && (
        <div
          id={nav.listboxId}
          role="listbox"
          aria-label="Vehicle suggestions"
          className={cx(
            styles.menu,
            clearOnSelect ? styles.menuFull : styles.menuFixed,
          )}
        >
          {matches.length === 0 ? (
            <p className={styles.empty}>
              {options.length === 0
                ? "No vehicles left — manage the fleet under Settings."
                : "No matching vehicles."}
            </p>
          ) : (
            matches.map((v, i) => (
              <button
                key={v.id}
                id={nav.optionId(i)}
                type="button"
                role="option"
                aria-selected={i === nav.activeIndex}
                tabIndex={-1}
                onClick={() => pick(v)}
                className={cx(
                  styles.option,
                  i === nav.activeIndex && styles.optionActive,
                )}
              >
                {v.name}
                {v.vehicle_type && (
                  <span className={styles.optionType}>{v.vehicle_type}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
